import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as OkIcon,
  CloudQueue as InfraIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Error as FailIcon,
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Sync as VerifyIcon,
  WarningAmber as WarnIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';
import SettingsErrorAlert from './SettingsErrorAlert';

const ENVIRONMENTS = [
  { value: 'development', label: 'Development', short_name: 'dev' },
  { value: 'production', label: 'Production', short_name: 'prod' },
  { value: 'nonproduction', label: 'Non-Production', short_name: 'nonprod' },
  { value: 'staging', label: 'Staging', short_name: 'staging' },
  { value: 'stress', label: 'Stress', short_name: 'stress' },
  { value: 'test', label: 'Test', short_name: 'test' },
  { value: 'release', label: 'Release', short_name: 'release' }
];

const shortFor = (purpose) =>
  ENVIRONMENTS.find((e) => e.value === purpose)?.short_name || 'dev';

const emptyForm = {
  id: '',
  label: '',
  short_name: 'dev',
  purpose: 'development',
  provider: 'azure',
  cluster_name: '',
  region: '',
  resource_group: '',
  api_server: '',
  dashboard_url: '',
  auth_type: 'kubeconfig',
  enabled: true,
  notes: '',
  kubeconfig: '',
  token: '',
  kubeconfig_set: false,
  token_set: false
};

const purposeColor = {
  development: '#0284c7',
  production: '#dc2626',
  nonproduction: '#7c3aed',
  staging: '#ca8a04',
  stress: '#ea580c',
  test: '#0891b2',
  release: '#0d9488'
};

const ConnectionBadge = ({ status, error, version }) => {
  if (status === 'ok') {
    return (
      <Tooltip title={version ? `Connected · Kubernetes ${version}` : 'Connection verified'}>
        <Chip
          size="small"
          icon={<OkIcon sx={{ color: '#16a34a !important' }} />}
          label="Connected"
          sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }}
        />
      </Tooltip>
    );
  }
  if (status === 'failed') {
    return (
      <Tooltip title={error || 'Connection failed — verify credentials'}>
        <Chip
          size="small"
          icon={<FailIcon sx={{ color: '#dc2626 !important' }} />}
          label="Verify connection"
          sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700 }}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Not verified yet">
      <Chip
        size="small"
        icon={<WarnIcon sx={{ color: '#ca8a04 !important' }} />}
        label="Unchecked"
        sx={{ bgcolor: '#fef9c3', color: '#854d0e', fontWeight: 700 }}
      />
    </Tooltip>
  );
};

const InfrastructureSettings = () => {
  const { can } = usePermissions();
  const canEdit = can('settings', 'edit');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifyingId, setVerifyingId] = useState('');
  const [error, setError] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [credentialsSecret, setCredentialsSecret] = useState('tenanthub-k8s-infra-secrets');
  const [k8sSecretsAvailable, setK8sSecretsAvailable] = useState(false);
  const [environments, setEnvironments] = useState(ENVIRONMENTS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const applyPayload = (data) => {
    setClusters(data.clusters || []);
    setCredentialsSecret(data.credentials_secret || 'tenanthub-k8s-infra-secrets');
    setK8sSecretsAvailable(Boolean(data.k8s_secrets_available));
    if (Array.isArray(data.environments) && data.environments.length) {
      setEnvironments(data.environments);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/settings/infrastructure');
      applyPayload(response.data.data || {});
    } catch (err) {
      setError({
        message: err.response?.data?.message || err.message || 'Failed to load infrastructure settings',
        path: err.response?.data?.path,
        status: err.response?.status
      });
      setClusters([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setDialogMode('add');
    setForm({ ...emptyForm, short_name: shortFor('development') });
    setDialogOpen(true);
  };

  const openEdit = (cluster) => {
    setDialogMode('edit');
    setForm({
      ...emptyForm,
      ...cluster,
      short_name: shortFor(cluster.purpose),
      kubeconfig: '',
      token: ''
    });
    setDialogOpen(true);
  };

  const onFormChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'purpose') {
        next.short_name = shortFor(value);
      }
      if (field === 'label' && !prev.cluster_name) {
        next.cluster_name = String(value).toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 63);
      }
      return next;
    });
  };

  const secretNote = useMemo(
    () => (
      `This value and any other confidential credentials are stored in the Kubernetes Secret “${credentialsSecret}” `
      + 'in the cluster where TenantHub Super Admin is running — never in the application database.'
    ),
    [credentialsSecret]
  );

  const submitDialog = async () => {
    if (!canEdit) return;
    if (!form.label.trim()) {
      toast.error('Label is required');
      return;
    }
    if (!form.cluster_name.trim()) {
      toast.error('Cluster name is required');
      return;
    }
    if (dialogMode === 'add' && form.auth_type === 'kubeconfig' && !form.kubeconfig.trim()) {
      toast.error('Paste a kubeconfig to verify and store');
      return;
    }
    if (dialogMode === 'add' && form.auth_type === 'token' && !form.token.trim()) {
      toast.error('Paste a cluster token to verify and store');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        purpose: form.purpose,
        provider: form.provider,
        cluster_name: form.cluster_name.trim(),
        region: form.region.trim(),
        resource_group: form.resource_group.trim(),
        api_server: form.api_server.trim(),
        dashboard_url: form.dashboard_url.trim(),
        auth_type: form.auth_type,
        enabled: Boolean(form.enabled),
        notes: form.notes.trim(),
        ...(form.kubeconfig.trim() ? { kubeconfig: form.kubeconfig.trim() } : {}),
        ...(form.token.trim() ? { token: form.token.trim() } : {})
      };

      let connection = null;
      if (dialogMode === 'add') {
        const response = await api.post('/settings/infrastructure/clusters', payload);
        connection = response.data.data?.connection;
      } else {
        const response = await api.put(`/settings/infrastructure/clusters/${form.id}`, payload);
        connection = response.data.data?.connection;
      }

      if (connection?.ok) {
        toast.success('Saved — connection verified');
      } else {
        toast.warn(connection?.error || 'Saved, but connection could not be verified. Use Verify.');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save cluster');
    } finally {
      setSaving(false);
    }
  };

  const verifyCluster = async (cluster) => {
    if (!canEdit) return;
    setVerifyingId(cluster.id);
    try {
      const response = await api.post(`/settings/infrastructure/clusters/${cluster.id}/verify`);
      const connection = response.data.data?.connection;
      if (connection?.ok) toast.success(`Connected · ${connection.kubernetes_version || 'OK'}`);
      else toast.error(connection?.error || 'Connection verification failed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to verify connection');
    } finally {
      setVerifyingId('');
    }
  };

  const confirmDelete = async () => {
    if (!canEdit || !deleteTarget) return;
    setSaving(true);
    try {
      const response = await api.delete(`/settings/infrastructure/clusters/${deleteTarget.id}`);
      applyPayload(response.data.data || {});
      setDeleteTarget(null);
      toast.success('Cluster removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete cluster');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <InfraIcon sx={{ color: '#0f766e', fontSize: 36 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Infrastructure
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Register Kubernetes clusters for automated tenant provisioning. Credentials are stored in
              Secret
              {' '}
              <code>{credentialsSecret}</code>
              {k8sSecretsAvailable ? '' : ' (persisted when TenantHub runs in-cluster)'}.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={saving}>Refresh</Button>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
              sx={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' }}
            >
              Add Kubernetes Cluster (k8s)
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <SettingsErrorAlert message={error.message} path={error.path} status={error.status} />
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        When creating a tenant, choose one of these clusters so deployment targets the correct Kubernetes infrastructure.
        A green check means the API connection was verified; red means you should verify credentials.
      </Alert>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Connection</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Short</TableCell>
              <TableCell>Environment / Purpose</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Cluster</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clusters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Alert severity="info">
                    No clusters yet. Click “Add Kubernetes Cluster (k8s)” to register Dev, Prod, or other environments.
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              clusters.map((c) => {
                const dashUrl = c.dashboard_url_resolved || c.dashboard_url;
                return (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <ConnectionBadge
                        status={c.connection_status}
                        error={c.connection_error}
                        version={c.kubernetes_version}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700}>{c.label}</Typography>
                      {c.region ? (
                        <Typography variant="caption" color="text.secondary">{c.region}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={c.short_name} sx={{ fontFamily: 'monospace' }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={environments.find((e) => e.value === c.purpose)?.label || c.purpose}
                        sx={{
                          bgcolor: `${purposeColor[c.purpose] || '#64748b'}22`,
                          color: purposeColor[c.purpose] || '#64748b',
                          fontWeight: 700
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ textTransform: 'uppercase' }}>{c.provider}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{c.cluster_name}</Typography>
                      {c.resource_group ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          rg: {c.resource_group}
                        </Typography>
                      ) : null}
                      {c.connection_status === 'failed' && c.connection_error ? (
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                          {c.connection_error}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={c.enabled ? 'Yes' : 'No'}
                        color={c.enabled ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {dashUrl ? (
                        <Tooltip title="Open Kubernetes / cloud dashboard">
                          <IconButton
                            size="small"
                            component="a"
                            href={dashUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {canEdit && (
                        <>
                          <Tooltip title="Verify connection">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => verifyCluster(c)}
                                disabled={verifyingId === c.id}
                              >
                                {verifyingId === c.id
                                  ? <CircularProgress size={16} />
                                  : <VerifyIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(c)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(c)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add Kubernetes Cluster (k8s)' : `Edit: ${form.label}`}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                required
                label="Label"
                value={form.label}
                onChange={onFormChange('label')}
                placeholder="Pilvi Dev AKS"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Environment / Purpose</InputLabel>
                <Select
                  label="Environment / Purpose"
                  value={form.purpose}
                  onChange={onFormChange('purpose')}
                >
                  {environments.map((env) => (
                    <MenuItem key={env.value} value={env.value}>
                      {env.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Short name"
                value={form.short_name}
                InputProps={{ readOnly: true }}
                helperText="From environment"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Provider</InputLabel>
                <Select label="Provider" value={form.provider} onChange={onFormChange('provider')}>
                  <MenuItem value="azure">Azure (AKS)</MenuItem>
                  <MenuItem value="gcp">GCP (GKE)</MenuItem>
                  <MenuItem value="aws">AWS (EKS)</MenuItem>
                  <MenuItem value="local">Local / Docker Desktop</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                required
                label="Cluster name"
                value={form.cluster_name}
                onChange={onFormChange('cluster_name')}
                placeholder="aks-dev-01"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Region"
                value={form.region}
                onChange={onFormChange('region')}
                placeholder="eastus"
              />
            </Grid>
            {form.provider === 'azure' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Resource group"
                  value={form.resource_group}
                  onChange={onFormChange('resource_group')}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="API server (optional)"
                value={form.api_server}
                onChange={onFormChange('api_server')}
                placeholder="https://...."
                helperText="Usually taken from kubeconfig; required for token auth"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Dashboard URL (optional)"
                value={form.dashboard_url}
                onChange={onFormChange('dashboard_url')}
                placeholder="https://portal.azure.com/..."
                helperText="Opens from the Open dashboard action; cloud console is used if empty"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Auth type</InputLabel>
                <Select label="Auth type" value={form.auth_type} onChange={onFormChange('auth_type')}>
                  <MenuItem value="kubeconfig">Kubeconfig</MenuItem>
                  <MenuItem value="token">Token</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={Boolean(form.enabled)} onChange={onFormChange('enabled')} />}
                label="Enabled for Add Tenant"
              />
            </Grid>
            {form.auth_type === 'kubeconfig' ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  required={dialogMode === 'add'}
                  label="Kubeconfig"
                  value={form.kubeconfig}
                  onChange={onFormChange('kubeconfig')}
                  placeholder={form.kubeconfig_set ? '•••• leave blank to keep existing' : 'Paste kubeconfig YAML'}
                  helperText={secretNote}
                  FormHelperTextProps={{ sx: { mx: 0 } }}
                  sx={{ '& textarea': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8rem' } }}
                />
                <Alert severity="info" sx={{ mt: 1 }}>
                  {secretNote}
                  {' '}
                  Connection is verified automatically when you save.
                </Alert>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  type="password"
                  required={dialogMode === 'add'}
                  label="Cluster token"
                  value={form.token}
                  onChange={onFormChange('token')}
                  placeholder={form.token_set ? '•••• leave blank to keep existing' : 'Paste bearer token'}
                  helperText={secretNote}
                />
                <Alert severity="info" sx={{ mt: 1 }}>
                  {secretNote}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" value={form.notes} onChange={onFormChange('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitDialog}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            {dialogMode === 'add' ? 'Add & verify' : 'Save & verify'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete cluster</DialogTitle>
        <DialogContent>
          Remove
          {' '}
          <strong>{deleteTarget?.label}</strong>
          ? Credentials will also be deleted from the Kubernetes Secret.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={saving}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InfrastructureSettings;
